import type { FC, ReactNode, ComponentType } from 'react';
import classNames from 'classnames';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import SmartOnboardingProgressBar, {
  type ProgressSegment,
} from './SmartOnboardingProgressBar';

interface ActivationStepWrapperProps {
  children: ReactNode;
  className?: string;

  // Progress bar
  showProgressBar?: boolean;
  progressSegments?: ProgressSegment[];
  onProgressSegmentClick?: (segmentId: string) => void;

  // Header content
  title?: string;
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;

  // Back button
  showBackButton?: boolean;
  onBack?: () => void;
}

/**
 * Unified wrapper for banking signup steps
 * Handles:
 * - Smart progress bar (optional, with automatic positioning)
 * - Title and subtitle
 * - Optional icon
 * - Back button
 * - Consistent padding and layout
 *
 * This replaces the need to manually add OnboardingStepWrapper, heading and subheading in each step component.
 */
const OnboardingStepWrapper: FC<ActivationStepWrapperProps> = ({
  children,
  className = '',
  showProgressBar = true,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon: Icon,
  showBackButton = false,
  onBack,
}) => {
  return (
    <div
      className={classNames('w-full max-w-2xl mx-auto px-6 py-4', className)}
    >
      {/* Progress Bar with Back Button */}
      {showProgressBar && progressSegments && progressSegments.length > 0 && (
        <div className="relative mb-6">
          {/* Back Button - positioned absolutely on the left */}
          {showBackButton && onBack && (
            <Button
              asDefault
              className="absolute left-0 top-1/2 -translate-y-1/2 flex !items-center space-x-2 group"
              onClick={onBack}
            >
              <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />
            </Button>
          )}

          {/* Progress Bar - centered with more spacing */}
          <div className="mx-8">
            <SmartOnboardingProgressBar
              segments={progressSegments}
              onSegmentClick={onProgressSegmentClick}
            />
          </div>
        </div>
      )}

      {/* Back Button (when no progress bar) */}
      {(!showProgressBar ||
        !progressSegments ||
        progressSegments.length === 0) &&
        showBackButton &&
        onBack && (
          <Button
            asDefault
            className="flex !items-center space-x-2 group mb-4"
            onClick={onBack}
          >
            <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />
          </Button>
        )}

      {/* Icon */}
      {Icon && (
        <div className="flex justify-center mb-4">
          <Icon className="w-12 h-12 text-bank" />
        </div>
      )}

      {/* Title and Subtitle */}
      {title && (
        <div className="mb-6">
          <h2 className="font-degular text-2xl font-bold text-center mb-2">
            {title}
          </h2>
          {subtitle && (
            <p className="text-primary-600 text-center max-w-lg mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Step Content */}
      {children}
    </div>
  );
};

export default OnboardingStepWrapper;
